/**
 * Class Session Results
 * ---------------------
 * What happened in one class session, read top-down the way a teacher asks:
 *
 *   1. What happened?        session summary and headline figures
 *   2. How were marks spread? score distribution
 *   3. Where did it go wrong? question-by-question analytics
 *   4. Who needs attention?   every student, including those who never submitted
 *
 * Reads by URL param rather than ClassSessionContext, because endSession()
 * clears the active session before this screen mounts. Figures come from
 * lib/analytics/metrics.ts; nothing is computed here.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionResults, type RosterEntry } from '../../hooks/useSessionResults';
import {
  distribution,
  formatDuration,
  formatPercent,
  latestPerStudent,
  questionStats,
  scoreTone,
  summarise,
  type Result,
} from '../../lib/analytics/metrics';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
import { Skeleton } from '../../Components/ui/skeleton';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { MetricCard, MetricGrid } from '../../Components/analytics/MetricCard';
import { DistributionChart } from '../../Components/analytics/charts';
import { DataTable, type Column } from '../../Components/analytics/DataTable';
import { QuestionAnalytics } from '../../Components/analytics/QuestionAnalytics';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsNote,
  AnalyticsPanel,
  ChartSkeleton,
  TableSkeleton,
} from '../../Components/analytics/AnalyticsPanel';

interface StudentRow {
  uid: string;
  name: string;
  email: string;
  result: Result | null;
  attendance: RosterEntry | null;
}

const formatDateTime = (ms: number | null) =>
  ms === null
    ? '—'
    : new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function statusOf(row: StudentRow): { label: string; tone: string } {
  if (row.attendance?.removed) return { label: 'Removed', tone: 'text-muted-foreground' };
  if (!row.result) return { label: 'Not submitted', tone: 'text-muted-foreground' };
  if (row.result.attempted === 0) return { label: 'No answers', tone: 'text-amber-500' };
  return row.result.status === 'completed'
    ? { label: 'Completed', tone: 'text-foreground' }
    : { label: 'Partial', tone: 'text-amber-500' };
}

const ClassSessionResults = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const data = useSessionResults(sessionId);
  const [lessonKey, setLessonKey] = useState<string | null>(null);

  // Default to the last lesson taught — usually the one the teacher just ended.
  useEffect(() => {
    if (data.lessons.length > 0 && !data.lessons.some((l) => l.key === lessonKey)) {
      setLessonKey(data.lessons[data.lessons.length - 1].key);
    }
  }, [data.lessons, lessonKey]);

  const lesson = data.lessons.find((l) => l.key === lessonKey) ?? null;
  // One result per student: a retake within the session replaces the first try.
  const results = useMemo(() => (lesson ? latestPerStudent(lesson.results) : []), [lesson]);
  const summary = useMemo(() => summarise(results, data.passThreshold), [results, data.passThreshold]);
  const bands = useMemo(() => distribution(results), [results]);
  const questions = useMemo(() => questionStats(results), [results]);

  const rows = useMemo<StudentRow[]>(() => {
    const byUid = new Map<string, StudentRow>();
    for (const a of data.roster) {
      byUid.set(a.uid, { uid: a.uid, name: a.name, email: a.email, result: null, attendance: a });
    }
    for (const r of results) {
      const existing = byUid.get(r.studentId);
      if (existing) existing.result = r;
      else byUid.set(r.studentId, { uid: r.studentId, name: r.studentName || 'Student', email: '', result: r, attendance: null });
    }
    return [...byUid.values()];
  }, [data.roster, results]);

  const joined = data.roster.filter((r) => !r.removed).length;
  const submitted = rows.filter((r) => r.result && r.result.attempted > 0).length;
  const completedAll = rows.filter((r) => r.result?.status === 'completed').length;

  const columns: Column<StudentRow>[] = [
    {
      key: 'student',
      header: 'Student',
      sortValue: (r) => r.name,
      cell: (r) => (
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium text-foreground">
            <Link
              to={`/analytics/students/${r.uid}`}
              onClick={(e) => e.stopPropagation()}
              className="truncate hover:text-primary hover:underline"
            >
              {r.name}
            </Link>
            {r.attendance?.removed && <Badge variant="outline" className="text-[10px]">removed</Badge>}
          </p>
          {r.email && <p className="truncate text-xs text-muted-foreground">{r.email}</p>}
        </div>
      ),
    },
    {
      key: 'score',
      header: 'Score',
      align: 'right',
      sortValue: (r) => r.result?.marksObtained ?? null,
      cell: (r) => (r.result ? `${r.result.marksObtained} / ${r.result.maximumMarks}` : '—'),
    },
    {
      key: 'percentage',
      header: 'Percentage',
      align: 'right',
      sortValue: (r) => (r.result && r.result.attempted > 0 ? r.result.percentage : null),
      cell: (r) =>
        r.result && r.result.attempted > 0 ? (
          <span className={`font-semibold ${scoreTone(r.result.percentage, data.passThreshold)}`}>{formatPercent(r.result.percentage)}</span>
        ) : (
          '—'
        ),
    },
    {
      key: 'accuracy',
      header: 'Accuracy',
      align: 'right',
      sortValue: (r) => r.result?.accuracy ?? null,
      cell: (r) => formatPercent(r.result?.accuracy),
    },
    {
      key: 'attempted',
      header: 'Attempted',
      align: 'right',
      sortValue: (r) => r.result?.attempted ?? null,
      cell: (r) => (r.result ? `${r.result.attempted} / ${r.result.totalQuestions}` : '—'),
    },
    {
      key: 'time',
      header: 'Time',
      align: 'right',
      hideOnMobile: true,
      sortValue: (r) => r.result?.timeSeconds ?? r.attendance?.durationSeconds ?? null,
      cell: (r) => formatDuration(r.result?.timeSeconds ?? r.attendance?.durationSeconds),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => statusOf(r).label,
      cell: (r) => {
        const s = statusOf(r);
        return <span className={`text-xs font-medium ${s.tone}`}>{s.label}</span>;
      },
    },
    {
      key: 'view',
      header: '',
      hideOnMobile: true,
      cell: (r) =>
        r.result?.id ? (
          <Link to={`/results/${r.result.id}`} onClick={(e) => e.stopPropagation()} className="text-xs font-medium text-primary hover:underline">
            View
          </Link>
        ) : null,
    },
  ];

  const exportCsv = () => {
    const header = ['Student', 'Email', 'Lesson', 'Marks', 'Maximum', 'Percent', 'Accuracy', 'Attempted', 'Questions', 'Time (s)', 'Status', 'Attempt', 'Joined'];
    const lines = rows.map((r) =>
      [
        r.name,
        r.email,
        lesson?.title ?? '',
        r.result?.marksObtained ?? '',
        r.result?.maximumMarks ?? '',
        r.result && r.result.attempted > 0 ? r.result.percentage : '',
        r.result?.accuracy ?? '',
        r.result?.attempted ?? '',
        r.result?.totalQuestions ?? '',
        r.result?.timeSeconds ?? r.attendance?.durationSeconds ?? '',
        statusOf(r).label,
        r.result?.attempt ?? '',
        r.attendance?.joinedAt ? new Date(r.attendance.joinedAt).toISOString() : '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class-results-${sessionId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const backTo =
    profile?.role === 'partner'
      ? '/dashboard/partner'
      : profile?.role === 'school'
        ? '/dashboard/school'
        : profile?.role === 'principal'
          ? '/dashboard/principal'
          : '/dashboard/teacher';

  const title = lesson?.title ?? data.session?.launched_lesson?.title ?? 'Lesson results';
  const loading = data.loading;

  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-6xl space-y-5 px-4 sm:px-6">
        <header>
          <Link to={backTo} className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </Link>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Class session results</p>
              {loading && !data.session ? (
                <Skeleton className="mt-2 h-7 w-64" />
              ) : (
                <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
                  {title}
                </h1>
              )}
            </div>
            {rows.length > 0 && (
              <Button variant="outline" onClick={exportCsv} className="gap-2">
                <Download className="h-3.5 w-3.5" /> Export CSV
              </Button>
            )}
          </div>

          {/* Session summary */}
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl border border-border bg-card p-4 text-sm sm:grid-cols-3 lg:grid-cols-6">
            {[
              ['Class', data.className ?? '—'],
              ['Teacher', data.teacherName ?? '—'],
              ['Date', formatDateTime(data.startedAt)],
              ['Duration', data.session?.status === 'ended' ? formatDuration(data.durationSeconds) : data.session ? 'In progress' : '—'],
              ['Students joined', String(joined)],
              ['Submitted', `${submitted}${joined > 0 ? ` of ${joined}` : ''}`],
            ].map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
                <dd className="mt-0.5 truncate font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </header>

        {data.error && <AnalyticsErrorState message={data.error} />}

        {data.lessons.length > 1 && (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Lessons in this session">
            {data.lessons.map((l) => (
              <button
                key={l.key}
                type="button"
                role="tab"
                aria-selected={l.key === lessonKey}
                onClick={() => setLessonKey(l.key)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                  l.key === lessonKey ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.title}
              </button>
            ))}
          </div>
        )}

        {/* 1. What happened */}
        <MetricGrid>
          <MetricCard
            loading={loading}
            label="Class average"
            value={formatPercent(summary.average)}
            detail={summary.validSubmissions > 0 ? `${summary.validSubmissions} submission${summary.validSubmissions === 1 ? '' : 's'}` : 'No submissions yet'}
            definition="Mean of each student's percentage, over students who answered at least one question."
          />
          <MetricCard
            loading={loading}
            label="Highest score"
            value={formatPercent(summary.highest?.percentage)}
            detail={summary.highest ? `${summary.highest.marksObtained} / ${summary.highest.maximumMarks} marks` : undefined}
            definition="Highest percentage among submissions."
          />
          <MetricCard
            loading={loading}
            label="Median"
            value={formatPercent(summary.median)}
            detail={summary.lowest ? `Lowest ${formatPercent(summary.lowest.percentage)}` : undefined}
            definition="The middle percentage when submissions are ordered; less affected by outliers than the average."
          />
          <MetricCard
            loading={loading}
            label="Completion"
            value={joined > 0 ? formatPercent((completedAll / joined) * 100) : '—'}
            detail={joined > 0 ? `${completedAll} of ${joined} answered every question` : undefined}
            definition="Students who answered every question, out of students who joined."
          />
          <MetricCard
            loading={loading}
            label="Accuracy"
            value={formatPercent(summary.accuracy)}
            detail="Correct of attempted"
            definition="Mean of each student's correct answers ÷ questions attempted. Unanswered questions don't count against it."
          />
          <MetricCard
            loading={loading}
            label="Pass rate"
            value={formatPercent(summary.passRate)}
            detail={`At or above ${data.passThreshold}%`}
            definition={`Share of submissions scoring at least the school's pass mark of ${data.passThreshold}%.`}
          />
        </MetricGrid>

        <div className="grid gap-5 lg:grid-cols-5">
          {/* 2. How marks were spread */}
          <AnalyticsPanel className="lg:col-span-2" title="Score distribution" description="Students in each percentage band">
            {loading ? (
              <ChartSkeleton height={220} />
            ) : summary.validSubmissions === 0 ? (
              <AnalyticsEmptyState compact description="The distribution appears once students submit the quiz." />
            ) : (
              <DistributionChart bands={bands} />
            )}
          </AnalyticsPanel>

          {/* 3. Where it went wrong */}
          <AnalyticsPanel className="lg:col-span-3" title="Question analytics" description="How the class answered each question">
            {loading ? <TableSkeleton rows={4} /> : <QuestionAnalytics questions={questions.questions} coverage={questions.coverage} totalResults={results.length} />}
          </AnalyticsPanel>
        </div>

        {/* 4. Who needs attention */}
        <AnalyticsPanel title="Student results" description="Everyone who joined, including students who did not submit">
          {loading ? (
            <TableSkeleton rows={6} />
          ) : (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(r) => r.uid}
              initialSort={{ key: 'student', direction: 'asc' }}
              searchPlaceholder="Search students"
              searchText={(r) => `${r.name} ${r.email}`}
              onRowClick={(r) => r.result?.id && navigate(`/results/${r.result.id}`)}
              empty={<AnalyticsEmptyState title="No students joined this session" description="Results appear here once students join and complete the lesson." />}
            />
          )}
          {data.source === 'live_report' && rows.some((r) => r.result) && (
            <AnalyticsNote>
              This session was held before results were linked to sessions, so figures come from the live classroom report. Per-question
              text and correct answers aren’t available for it.
            </AnalyticsNote>
          )}
          <AnalyticsNote>
            Each student’s latest attempt is counted. Guest and partner-demo students are not graded.
          </AnalyticsNote>
        </AnalyticsPanel>
      </div>
    </div>
  );
};

export default ClassSessionResults;
